import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { 
  ArrowLeft, 
  School, 
  User, 
  Mail, 
  Lock, 
  Building2,
  ExternalLink,
  CheckCircle2,
  Loader2,
  Info,
  BookOpen
} from 'lucide-react';
import api from '../services/api';

const RegistrationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // School Info
    school_name: '',
    school_route: '',
    // Admin Info
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
  });
  const [errors, setErrors] = useState({});

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.school_name.trim()) newErrors.school_name = 'School name is required';
    if (!formData.school_route.trim()) newErrors.school_route = 'School route is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (formData.password !== formData.confirm_password) newErrors.confirm_password = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setLoading(true);
    try {
      await api.post('/register-school', {
        school_name: formData.school_name,
        school_route: formData.school_route,
        admin_full_name: formData.full_name,
        admin_email: formData.email,
        admin_password: formData.password,
      });

      toast.success('Registration successful! You can now log in.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/login" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Login
            </Link>
            <h1 className="font-outfit text-3xl font-bold text-slate-900 mb-2">
              Register Your School
            </h1>
            <p className="text-slate-500">
              Get started with PTC Builder - 14-day free trial included
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                step >= 1 ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                <Building2 className="h-5 w-5" />
              </div>
              <div className={`w-24 h-1 ${step >= 2 ? 'bg-indigo-600' : 'bg-slate-200'}`} />
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                step >= 2 ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                <User className="h-5 w-5" />
              </div>
            </div>
          </div>
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-16 text-sm">
              <span className={step >= 1 ? 'text-indigo-600 font-medium' : 'text-slate-400'}>
                School Information
              </span>
              <span className={step >= 2 ? 'text-indigo-600 font-medium' : 'text-slate-400'}>
                Admin Account
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Step 1: School Information */}
            {step === 1 && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center gap-2">
                    <School className="h-5 w-5 text-indigo-600" />
                    School Information
                  </CardTitle>
                  <CardDescription>
                    Enter your school's details to create an account
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="school_name">School Name *</Label>
                    <Input
                      id="school_name"
                      placeholder="e.g., Springfield Academy"
                      value={formData.school_name}
                      onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                      className={errors.school_name ? 'border-red-500' : ''}
                      data-testid="school-name-input"
                    />
                    {errors.school_name && <p className="text-sm text-red-500">{errors.school_name}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="school_route">Veracross School Route *</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 text-sm">https://axiom.veracross.com/</span>
                      <Input
                        id="school_route"
                        placeholder="yourschool"
                        value={formData.school_route}
                        onChange={(e) => setFormData({ ...formData, school_route: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                        className={`w-40 ${errors.school_route ? 'border-red-500' : ''}`}
                        maxLength={25}
                        data-testid="school-route-input"
                      />
                    </div>
                    {errors.school_route && <p className="text-sm text-red-500">{errors.school_route}</p>}
                    <p className="text-xs text-slate-500">
                      This is your school's Veracross route (found after axiom.veracross.com/ in your Veracross URL)
                    </p>
                  </div>

                  {/* Veracross Setup Instructions */}
                  <div className="mt-6 pt-6 border-t">
                    <Accordion type="single" collapsible>
                      <AccordionItem value="veracross-setup">
                        <AccordionTrigger className="text-sm font-medium">
                          <span className="flex items-center gap-2">
                            <Info className="h-4 w-4 text-indigo-600" />
                            Veracross Setup Instructions
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-slate-600 space-y-4">
                          <div className="bg-indigo-50 p-4 rounded-lg">
                            <h4 className="font-medium text-indigo-900 mb-2">Before you begin:</h4>
                            <p className="text-indigo-700">
                              You'll need to enable PTC Builder access in your Veracross instance. 
                              This requires Veracross administrator privileges and the "OAuth_App_Admin" Security role.
                            </p>
                          </div>

                          <div className="space-y-3">
                            <h4 className="font-medium">Step 1: Create API Credentials in Veracross</h4>
                            <ol className="list-decimal list-inside space-y-2 pl-2">
                              <li>Log into Veracross as an administrator</li>
                              <li>From the Rocketship, navigate to the <strong>System/IT</strong> menu and choose <strong>Identity & Access Management</strong></li>
                              <li>Under the SSO & Integrations Section, click <strong>"Add Integration Partner"</strong></li>
                              <li>Search for <strong>"PTC Builder by EDUx"</strong></li>
                              <li>Click on the <strong>Start Integration</strong> link
                                <p className="text-xs text-slate-500 mt-1">(Note: You must have the "OAuth_App_Admin" Security role enabled)</p>
                              </li>
                              <li>Enable the required scopes:
                                <ul className="list-disc list-inside ml-4 mt-1 text-slate-500">
                                  <li>List Staff/Faculty</li>
                                  <li>Read Staff/Faculty</li>
                                  <li>List Academics: Rooms</li>
                                  <li>Read Academics: Rooms</li>
                                  <li>List Academics: Departments</li>
                                  <li>Read Academics: Departments</li>
                                </ul>
                              </li>
                              <li>Save and copy the <strong>Client ID</strong> and <strong>Client Secret</strong></li>
                            </ol>
                          </div>

                          <div className="space-y-3">
                            <h4 className="font-medium">Step 2: Configure PTC Builder</h4>
                            <ol className="list-decimal list-inside space-y-2 pl-2">
                              <li>After registration, log into PTC Builder</li>
                              <li>Go to <strong>School Settings</strong></li>
                              <li>Enter your Veracross API credentials</li>
                              <li>Click <strong>"Test Connection"</strong> to verify</li>
                            </ol>
                          </div>

                          <div className="space-y-3">
                            <h4 className="font-medium">Step 3: Sync Your Data</h4>
                            <ol className="list-decimal list-inside space-y-2 pl-2">
                              <li>Navigate to the <strong>Teachers</strong> page</li>
                              <li>Click <strong>"Sync from Veracross"</strong></li>
                              <li>Your teachers, rooms, and school levels will be imported</li>
                            </ol>
                          </div>

                          <div className="bg-amber-50 p-4 rounded-lg">
                            <h4 className="font-medium text-amber-900 mb-1">Need Help?</h4>
                            <p className="text-amber-700 text-sm">
                              If you need assistance with Veracross API setup, contact your Veracross support representative 
                              or refer to the Veracross API documentation.
                            </p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button type="button" onClick={handleNextStep} className="bg-indigo-600 hover:bg-indigo-700">
                      Continue
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Admin Account */}
            {step === 2 && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center gap-2">
                    <User className="h-5 w-5 text-indigo-600" />
                    Create Admin Account
                  </CardTitle>
                  <CardDescription>
                    Set up your school administrator account
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      placeholder="e.g., John Smith"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className={errors.full_name ? 'border-red-500' : ''}
                      data-testid="full-name-input"
                    />
                    {errors.full_name && <p className="text-sm text-red-500">{errors.full_name}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="e.g., admin@school.edu"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={errors.email ? 'border-red-500' : ''}
                      data-testid="email-input"
                    />
                    {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
                    <p className="text-xs text-slate-500">This will be your login username</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Minimum 8 characters"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={errors.password ? 'border-red-500' : ''}
                      data-testid="password-input"
                    />
                    {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm Password *</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      placeholder="Confirm your password"
                      value={formData.confirm_password}
                      onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                      className={errors.confirm_password ? 'border-red-500' : ''}
                      data-testid="confirm-password-input"
                    />
                    {errors.confirm_password && <p className="text-sm text-red-500">{errors.confirm_password}</p>}
                  </div>

                  {/* Trial Info */}
                  <div className="bg-emerald-50 p-4 rounded-lg flex items-start gap-3 mt-4">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-emerald-900">14-Day Free Trial</h4>
                      <p className="text-sm text-emerald-700">
                        Your account includes a 14-day free trial with full access to all features. 
                        No credit card required to start.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button type="button" variant="outline" onClick={() => setStep(1)}>
                      Back
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-indigo-600 hover:bg-indigo-700"
                      disabled={loading}
                      data-testid="register-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Creating Account...
                        </>
                      ) : (
                        'Create Account'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </form>

          {/* Help Link */}
          <div className="text-center mt-6">
            <Link 
              to="/help" 
              className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-700"
            >
              <BookOpen className="h-4 w-4 mr-1" />
              View Documentation
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-100 border-t border-slate-200 py-4 px-8">
        <p className="text-center text-sm text-slate-500">
          © {currentYear} Borrow A Geek Limited Trading as EDUx. All Rights Reserved. | <Link to="/changelog" className="text-indigo-600 hover:text-indigo-700 hover:underline">Version 1.3</Link>
        </p>
      </footer>

      <Toaster position="top-right" />
    </div>
  );
};

export default RegistrationPage;
